/** This small entry executes before the application tree. Hidden Page Break
 * sections may delay ready; submit is always invalid until authorized data. */
export interface PreSubmitSdk {
  questionID: string | number;
  subscribe(
    event: string,
    callback: (data: unknown) => void,
    options?: { immediateCall: boolean }
  ): unknown;
  getWidgetSetting(name: string): unknown;
  getFieldsValueById(ids: string[], callback: (data: unknown) => void): void;
  listenFromField(
    input: string,
    event: 'change' | 'keyup',
    callback: (value: unknown) => void
  ): void;
  isWidgetRequired(): boolean;
  isWidgetOnBuilder(): boolean;
  sendData(data: { valid: boolean; value: string }): void;
  sendSubmit(data: { valid: boolean; value: string }): void;
  requestFrameResize(data: { height: number }): void;
}
type Host = Window & {
  JFCustomWidget?: PreSubmitSdk;
  __pcPreSubmitSdk?: Promise<{
    sdk: PreSubmitSdk;
    ready: Promise<unknown>;
    setSubmit: (fn: () => void) => void;
  }>;
};
export function loadPreSubmitSdk() {
  const host = window as Host;
  if (!host.__pcPreSubmitSdk)
    host.__pcPreSubmitSdk = new Promise((resolve, reject) => {
      const adopt = () => {
        const sdk = host.JFCustomWidget;
        if (!sdk) {
          reject(new Error('Form connection unavailable.'));
          return;
        }
        let submit = () => {
          sdk.sendSubmit({ valid: false, value: '' });
        };
        sdk.subscribe('submit', () => submit());
        const ready = new Promise<unknown>((accept) =>
          sdk.subscribe('ready', accept, { immediateCall: true })
        );
        resolve({
          sdk,
          ready,
          setSubmit: (fn) => {
            submit = fn;
          },
        });
      };
      if (host.JFCustomWidget) {
        adopt();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-custom-widgets.js';
      script.async = true;
      script.referrerPolicy = 'no-referrer';
      const timer = setTimeout(
        () => reject(new Error('Form connection unavailable. Reload this form to retry.')),
        15000
      );
      script.onload = () => {
        clearTimeout(timer);
        adopt();
      };
      script.onerror = () => {
        clearTimeout(timer);
        reject(new Error('Form connection unavailable.'));
      };
      document.head.appendChild(script);
    });
  return host.__pcPreSubmitSdk;
}
if (
  typeof window !== 'undefined' &&
  /^\/integrations\/jotform\/(?:jfi_[a-z0-9]{20}\/)?widget\/?$/.test(window.location.pathname)
) {
  // No rejection details or host parameters enter application telemetry.
  loadPreSubmitSdk().catch(() => {});
}
